import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface RecentPage {
  pathname: string;
  /** Pre-resolved label (last breadcrumb crumb), so the palette doesn't need
   *  to recompute route metadata at read time. */
  label: string;
  /** Last visit timestamp — used to order entries newest-first. */
  visitedAt: number;
}

interface RecentPagesState {
  items: RecentPage[];
  push: (entry: Omit<RecentPage, 'visitedAt'>) => void;
  clear: () => void;
}

const MAX = 8;

/**
 * Bounded ring of recently-visited pages, persisted to localStorage. The
 * command palette surfaces these as the first section when the query is
 * empty — gives muscle memory for "open the same student profile again"
 * without searching.
 *
 * Dedups by pathname (same path bumps the entry to the top and updates its
 * label) so revisits don't bloat the list. Paths that shouldn't bloat the
 * history (login, change-password, print views) are filtered at the
 * caller — keep this store pathname-agnostic.
 */
export const useRecentPagesStore = create<RecentPagesState>()(
  persist(
    (set) => ({
      items: [],
      push: ({ pathname, label }) =>
        set((s) => {
          const others = s.items.filter((x) => x.pathname !== pathname);
          const next = [{ pathname, label, visitedAt: Date.now() }, ...others];
          return { items: next.slice(0, MAX) };
        }),
      clear: () => set({ items: [] }),
    }),
    { name: 'school.recent-pages' },
  ),
);
